<template>
    <BModal ref="modal" :title="isNew ? $t('licenseModal.addTitle') : $t('licenseModal.editTitle')" centered no-close-on-backdrop
        header-class="modal-title" modal-class="no-border-modal" @hidden="onHidden">
        <form ref="formRef" novalidate @submit.prevent="submit">
            <div class="mb-2">
                <label for="license-key" class="form-label">{{ $t('licenseModal.key') }}</label>
                <input id="license-key" ref="keyInput" type="text" class="form-control" :value="key" @input="onKeyInput" :readonly="!isNew"
                    :placeholder="$t('licenseModal.keyPlaceholder')" autocomplete="off" spellcheck="false" required />
                <small v-if="checking" id="license-checking" class="form-text text-muted">
                    <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>{{ $t('licenseModal.keyChecking') }}
                </small>
            </div>
            <div class="mb-2">
                <label for="license-name" class="form-label">{{ $t('licenseModal.license') }}</label>
                <select id="license-name" ref="nameSelect" class="form-select" v-model="name" :disabled="limits == null" @change="onNameChange">
                    <option value="" disabled>{{ $t(isNew && limits == null ? 'licenseModal.selectAfterKey' : 'licenseModal.selectLicense') }}</option>
                    <option v-for="(item, index) in options" :key="index" :value="item.name">{{ item.type }}</option>
                </select>
            </div>
            <p v-if="error" id="license-error" class="text-danger mb-0">{{ error }}</p>
        </form>
        <template #footer>
            <div class="d-flex justify-content-between align-items-center w-100">
                <button type="button" class="btn btn-secondary" :disabled="saving" @click="close(null)">{{ $t('common.cancel') }}</button>
                <button type="button" class="btn btn-primary ms-2" :disabled="saving || checking" @click="submit">{{ $t('common.ok') }}</button>
            </div>
        </template>
    </BModal>
</template>

<script>
import { BModal } from 'bootstrap-vue-next';
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/userStore';
import { LICENSE_KEY_REGEX } from '@/models/license';

// Activation of a license on a LINK from its key, or change of its type.
// The key is checked with the backoffice, as soon as it is complete (scanned or typed) for a new license
// and at once for an existing one: the types it can be activated as fill the list.
// show() resolves with the license saved by the API, or null when cancelled.
export default {
    name: 'LicenseModal',
    components: {
        BModal
    },
    setup() {
        const { t } = useI18n();
        const userStore = useUserStore();
        const modal = ref(null);
        const formRef = ref(null);
        const keyInput = ref(null);
        const nameSelect = ref(null);
        const license = ref(null);
        const key = ref('');
        const name = ref('');
        const error = ref('');
        const saving = ref(false);
        // Limits of the checked key (null until a key has been checked), and the key they belong to
        const limits = ref(null);
        const checkedKey = ref('');
        const checking = ref(false);
        let target = null;
        let resolver = null;
        let checkId = 0;

        const isNew = computed(() => license.value == null);

        // The limits of the key; the current type stays selectable even when the backoffice does not list it anymore
        const options = computed(() => {
            const items = [...(limits.value || [])];
            if (license.value && limits.value && !items.some(item => item.name === license.value.name)) items.push(license.value);
            return items;
        });

        const show = (facilityId, hub, currentLicense) => {
            target = { facilityId, hub };
            license.value = currentLicense;
            key.value = currentLicense ? currentLicense.unique_id ?? '' : '';
            name.value = currentLicense ? currentLicense.name : '';
            error.value = '';
            saving.value = false;
            limits.value = null;
            checkedKey.value = '';
            checking.value = false;
            checkId++;
            modal.value.show();
            // An existing license is checked at once; without a key, only its current type is offered
            if (currentLicense) {
                if (LICENSE_KEY_REGEX.test(key.value)) checkKey();
                else limits.value = [];
            }
            return new Promise(resolve => { resolver = resolve; });
        };

        const close = (result) => {
            const resolve = resolver;
            resolver = null;
            modal.value.hide();
            if (resolve) resolve(result);
        };

        const onHidden = () => {
            if (resolver) close(null);
        };

        // Asks the backoffice the limits of the key, which fill the list; a new license with a single type selects it at once.
        // Resolves with true when the key is known.
        const checkKey = async () => {
            const current = key.value;
            if (checking.value && checkedKey.value === current) return false;
            const id = ++checkId;
            checking.value = true;
            checkedKey.value = current;
            error.value = '';
            try {
                const result = await userStore.getLicenseLimits(current);
                if (id !== checkId) return false;
                limits.value = result;
                if (isNew.value) name.value = result.length === 1 ? result[0].name : '';
                return true;
            } catch (message) {
                if (id !== checkId) return false;
                limits.value = null;
                if (isNew.value) name.value = '';
                error.value = message;
                return false;
            } finally {
                if (id === checkId) checking.value = false;
            }
        };

        // The app reads the key in upper case; a complete key is checked at once, a changed one forgets its limits
        const onKeyInput = (event) => {
            key.value = event.target.value.toUpperCase();
            event.target.setCustomValidity('');
            if (key.value === checkedKey.value) return;
            checkId++;
            checking.value = false;
            limits.value = null;
            checkedKey.value = '';
            name.value = '';
            error.value = '';
            if (LICENSE_KEY_REGEX.test(key.value)) checkKey();
        };

        const onNameChange = (event) => {
            event.target.setCustomValidity('');
        };

        const submit = async () => {
            if (saving.value || checking.value) return;
            key.value = key.value.trim().toUpperCase();
            await nextTick();
            keyInput.value.setCustomValidity(isNew.value && key.value !== '' && !LICENSE_KEY_REGEX.test(key.value) ? t('licenseModal.keyInvalid') : '');
            if (isNew.value && keyInput.value.checkValidity() && limits.value == null) {
                // Key validated before its check ran (Enter sent by a scanner): check it, the user confirms the type after
                if (!(await checkKey())) return;
                if (!name.value) {
                    nameSelect.value.setCustomValidity(t('licenseModal.selectRequired'));
                    formRef.value.reportValidity();
                }
                return;
            }
            nameSelect.value.setCustomValidity(name.value ? '' : t('licenseModal.selectRequired'));
            if (!formRef.value.checkValidity()) {
                formRef.value.reportValidity();
                return;
            }

            saving.value = true;
            error.value = '';
            try {
                const saved = isNew.value
                    ? await userStore.addLinkLicense(target.facilityId, target.hub, key.value, name.value)
                    : await userStore.updateLinkLicense(target.facilityId, target.hub, license.value, name.value);
                close(saved);
            } catch (message) {
                error.value = message;
            } finally {
                saving.value = false;
            }
        };

        return {
            modal,
            formRef,
            keyInput,
            nameSelect,
            isNew,
            options,
            key,
            name,
            error,
            saving,
            limits,
            checking,
            show,
            close,
            onHidden,
            onKeyInput,
            onNameChange,
            submit,
        };
    }
}
</script>
